import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import useApi from "../../hooks/useApi";
import { Box, Link, Skeleton, Stack, useMediaQuery } from "@mui/material";
import CollectionCover from "./Cover";
import { useTheme } from "@emotion/react";
import { useAuth } from "../../context/authContext";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";
import { Link as ReactRouterLink } from "react-router-dom";
import MotionInView from "../../components/MotionInView";

// The homepage's section entrance (see Home.js), but triggered sooner — just
// after a cover peeks in at the bottom of the screen — so the covers below
// the first are already arriving as you scroll to them
const ENTRANCE = { margin: "0px 0px -5% 0px", duration: 0.6 };

/**
 * Canonical-collections preview rendered on the homepage. Always read-only
 * (admin exception aside). Each cover fades and rises in as it scrolls into
 * view; `firstInView`, when given, drives the first one instead (e.g. to
 * arrive with the section heading).
 */
export default function CuratePreview({ firstInView }) {
  const { user } = useAuth();
  const demoPercentages = [32, 18, 44];
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { fetchExploreCollections } = useApi();
  const { openList } = useDetailNavigation();
  const [hintDismissed, setHintDismissed] = useState(false);

  // Open the tapped collection and dismiss the tap hint — once the user has tapped
  // a cover they know the interaction model (resets on page refresh).
  const handleCoverClick = (list) => {
    openList(list);
    setHintDismissed(true);
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["homepageCollections"],
    queryFn: () =>
      fetchExploreCollections({
        tagFilter: [[["display", "homepage"]]],
        perPage: 3,
        // Featured carousel — hide collections too sparse to make a convincing cover.
        minItems: 5,
        sortBy: "updated",
      }),
  });

  const collections = data?.items ?? [];

  if (isError) return null;

  if (isLoading)
    return (
      <Box
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
      </Box>
    );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Stack direction="column" spacing={2}>
        {collections.map((list, i) => (
          <MotionInView
            key={list.list_id}
            {...ENTRANCE}
            {...(i === 0 && firstInView !== undefined && {
              inView: firstInView,
            })}
          >
            <CollectionCover
              list={list}
              reverse={i % 2 === 0}
              onClick={() => handleCoverClick(list)}
              isMobile={fullscreen}
              hint={i === 0 && fullscreen && !hintDismissed}
              demoCompletion={
                user ? null : demoPercentages[i % demoPercentages.length]
              }
            />
          </MotionInView>
        ))}
        <Link
          component={ReactRouterLink}
          to="/explore"
          underline="hover"
          sx={{ fontWeight: "bold" }}
        >
          Explore more collections...
        </Link>
      </Stack>
    </Box>
  );
}
